import { useEffect, useState } from 'react';
import { PageSeo } from './PageSeo';
import { BackToToday, NarrowPage, PageHeader } from './SiteChrome';
import { SiteFooter } from './SiteFooter';
import { PAGE_META } from '../lib/seo';
import { getNewsUnreadCount, NEWS_SEEN_KEY } from '../lib/news';

const EXAMPLE_GUESSES = [
  { name: 'Dog', rank: 3, note: 'Way too obvious' },
  { name: 'Otter', rank: 58, note: 'Solid pick' },
  { name: 'Axolotl', rank: 96, note: 'Deep cut!' },
];

export function HowToPlayPage() {
  const [newsUnreadCount, setNewsUnreadCount] = useState(0);

  useEffect(() => {
    try {
      setNewsUnreadCount(getNewsUnreadCount(localStorage.getItem(NEWS_SEEN_KEY)));
    } catch {
      setNewsUnreadCount(0);
    }
  }, []);

  return (
    <NarrowPage>
    <div className="relative pb-8">
      <PageSeo meta={PAGE_META.howToPlay} />
      <PageHeader newsUnreadCount={newsUnreadCount} />

      <BackToToday />
      <div className="text-[11px] font-black uppercase tracking-[0.25em] text-foreground/40 mb-3">
        The rules
      </div>
      <h1 className="text-4xl md:text-5xl font-black tracking-tight leading-none mb-6">
        How to Play
      </h1>
      <div className="h-1.5 w-24 rounded-full bg-[linear-gradient(to_right,#fb923c,#facc15,#4ade80,#22d3ee)] mb-8" />

      <div className="space-y-8 text-[15px] leading-relaxed text-foreground/75">
        <section className="space-y-3">
          <h2 className="text-xl font-black tracking-tight text-foreground">
            Guess answers closest to #100
          </h2>
          <p>
            Every day, Centumth publishes one topic and a hidden Top 100. You get five guesses. The
            obvious answers sit near the top of the list. The hidden gems — the ones most people
            would not think of first — sit near #100.
          </p>
          <p>
            Your score is the sum of the ranks you find. Guess something ranked #3 and you earn 3
            points. Guess something ranked #96 and you earn 96. Miss the list entirely and that
            guess is worth 0. A perfect game is 490 points: 96 + 97 + 98 + 99 + 100.
          </p>
        </section>

        <section>
          <div className="flex justify-end mb-1.5">
            <div className="text-[10px] font-black tracking-widest bg-foreground text-background px-2 py-0.5 rounded-md">
              AIM HERE ↓
            </div>
          </div>
          <div className="h-3 rounded-full bg-[linear-gradient(to_right,#fb923c,#facc15,#4ade80,#22d3ee)]" />
          <div className="flex justify-between mt-2 text-[10px] font-black uppercase tracking-widest">
            <div className="text-left text-foreground/40">
              <div>#1</div>
              <div>Everyone knows it</div>
            </div>
            <div className="text-right text-foreground">
              <div>#100</div>
              <div>Hidden gem</div>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-black tracking-tight text-foreground">Example — Top 100 Animals</h2>
          <p>
            If the topic were animals, “Dog” is a terrible official guess: everyone names it, so it
            ranks near the top and scores almost nothing. “Axolotl” is the kind of answer the game
            is built around.
          </p>
          <div className="space-y-2">
            {EXAMPLE_GUESSES.map((g) => (
              <div
                key={g.name}
                className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-muted/40 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="font-bold text-foreground">{g.name}</div>
                  <div className="text-[11px] font-medium text-foreground/50">{g.note}</div>
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className="font-black tabular-nums text-foreground">+{g.rank}</div>
                  <div className="text-[11px] font-medium text-foreground/50">ranked #{g.rank}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-3 gap-3">
          <div className="rounded-2xl border border-border bg-muted/40 py-3 text-center">
            <div className="text-2xl font-black tabular-nums text-foreground">5</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">
              Guesses
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-muted/40 py-3 text-center">
            <div className="text-2xl font-black tabular-nums text-foreground">490</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">
              Max score
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-muted/40 py-3 text-center">
            <div className="text-2xl font-black text-foreground">Daily</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">
              New topic
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-black tracking-tight text-foreground">Details that help</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>Typos are forgiven. Close spellings still count when the match is obvious.</li>
            <li>You get one free hint per puzzle.</li>
            <li>Be specific. Abbreviations usually do not count.</li>
            <li>
              After your five official guesses, Arcade Mode lets you keep hunting the rest of the
              list. Your shared score stays locked to those first five.
            </li>
            <li>
              Every past topic lives in the archive, with the same rules as the day it was
              published.
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-black tracking-tight text-foreground">How the lists are made</h2>
          <p>
            Each Top 100 is researched and ranked by hand. Sources are the foundation — named
            wherever we can show them — and the final order may include small adjustments so the
            puzzle stays fair and fun. The goal is a list you can argue with, not a random trivia
            dump.
          </p>
        </section>
      </div>

      <SiteFooter />
    </div>
    </NarrowPage>
  );
}
